import React, { useEffect, useMemo, useState } from "react";
import UHeader from "@/custom_components/UHeader";
import PageContainer from "@/custom_components/PageContainer/PageContainer";
import SFIcon from "@/custom_components/SFIcon/SFIcon";
import Preloader from "@/custom_components/Preloader/Preloader";
import EmptyState from "@/custom_components/EmptyState/EmptyState";
import { withAuthentication } from "@/hoc/withAuthentication";
import HelpGroupHeader from "./HelpGroupHeader";
import FaqItem from "./FaqItem";
import TutorialCard from "./TutorialCard";
import { groupByIcon, matchesSearch } from "./helpUtils";
import classes from "./Help.module.css";

// Anything the tutorials and FAQs here don't answer is handled by the Zendesk
// chat bubble, which withAuthentication allowlists onto this route — there is
// deliberately no link out to the old help center.
const FAQ_SEARCH_FIELDS = ["question", "answer", "title"];
const TUTORIAL_SEARCH_FIELDS = ["title", "subText"];

// Both list endpoints return the entire collection with every field populated
// and no pagination, so one fetch per resource on mount is the whole feature —
// grouping, filtering and detail rendering all run off this cache.
const ENDPOINTS = {
  tutorials: "/help-content/tutorials",
  faqs: "/help-content/faqs",
};

/** Panel chrome shared by both columns: heading, scroll area, and states. */
const Panel = ({ title, subtitle, width, loading, error, onRetry, isEmpty, emptyState, children }) => (
  <PageContainer className={`custom_width ${classes.panel}`} style={{ "--width": width }}>
    <div className={classes.panelHead}>
      <div className="panel-title">{title}</div>
      <div className={classes.panelSub}>{subtitle}</div>
    </div>

    <div className={classes.scroll}>
      {loading && (
        <div className={classes.centered}>
          <Preloader />
        </div>
      )}

      {!loading && error && (
        <div className={classes.centered}>
          <EmptyState
            icon="warning"
            heading="Couldn't load this section"
            subtext={error}
            action={{ label: "Try again", onClick: onRetry }}
          />
        </div>
      )}

      {!loading && !error && isEmpty && <div className={classes.centered}>{emptyState}</div>}

      {!loading && !error && !isEmpty && children}
    </div>
  </PageContainer>
);

const Help = ({ httpClient }) => {
  const [tutorials, setTutorials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [errors, setErrors] = useState({ tutorials: null, faqs: null });
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [term, setTerm] = useState("");

  // Explicit open/closed decisions, keyed by FAQ id. Anything the user hasn't
  // touched follows `searching` (see isOpen below), so a search reveals its
  // own matches without stranding rows open once the term is cleared.
  const [overrides, setOverrides] = useState({});

  const searching = !!term.trim();

  useEffect(() => {
    document.body.classList.add("help-page");
    return () => document.body.classList.remove("help-page");
  }, []);

  // Toggles made while searching shouldn't linger once the search ends (and
  // vice versa) — reset the overrides whenever the default flips.
  useEffect(() => {
    setOverrides({});
  }, [searching]);

  useEffect(() => {
    let unmounted = false;
    setLoading(true);

    // allSettled, not all: a failure on one list shouldn't blank the other —
    // each column shows its own retry.
    Promise.allSettled([httpClient.get(ENDPOINTS.tutorials), httpClient.get(ENDPOINTS.faqs)]).then(
      ([tutorialsResult, faqsResult]) => {
        if (unmounted) return;

        // Both endpoints return a bare array (an empty one is `[]`, not a 404),
        // but coerce anyway so an unexpected shape can't crash the render.
        const rows = (result) => (Array.isArray(result.value) ? result.value : []);
        const reason = (result) =>
          result.status === "rejected"
            ? result.reason?.response?.data?.message ||
              result.reason?.message ||
              "Something went wrong."
            : null;

        setTutorials(rows(tutorialsResult));
        setFaqs(rows(faqsResult));
        setErrors({ tutorials: reason(tutorialsResult), faqs: reason(faqsResult) });
        setLoading(false);
      }
    );

    return () => {
      unmounted = true;
    };
  }, [httpClient, reloadKey]);

  // FAQs are one flat list in sortOrder — no grouping, the per-card icon carries
  // the category instead. The server already sorts ascending, and Array#sort is
  // stable, so this only guarantees the order rather than changing it: records
  // sharing a sortOrder keep the server's relative order (sortOrder is neither
  // unique nor contiguous, so ties are expected).
  const visibleFaqs = useMemo(
    () =>
      faqs
        .filter((faq) => matchesSearch(faq, term, FAQ_SEARCH_FIELDS))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [faqs, term]
  );

  const tutorialGroups = useMemo(
    () => groupByIcon(tutorials.filter((t) => matchesSearch(t, term, TUTORIAL_SEARCH_FIELDS))),
    [tutorials, term]
  );

  const faqMatches = visibleFaqs.length;
  const tutorialMatches = tutorialGroups.reduce((n, g) => n + g.items.length, 0);

  const isOpen = (id) => overrides[id] ?? searching;
  const toggle = (id) => setOverrides((prev) => ({ ...prev, [id]: !isOpen(id) }));

  const retry = () => setReloadKey((n) => n + 1);

  // Tutorials only — FAQs render flat. A single group means the heading is
  // restating the panel title, so it only earns its space once the content is
  // actually split across categories.
  const renderGroups = (groups, renderItem) =>
    groups.map((group) => (
      <div key={group.iconId} className={classes.group}>
        {groups.length > 1 && (
          <HelpGroupHeader iconId={group.iconId} label={group.label} count={group.items.length} />
        )}
        {group.items.map(renderItem)}
      </div>
    ));

  return (
    <section className={classes.page}>
      <UHeader title="Help & Support" align="left" />

      <div className={classes.searchRow}>
        <div className={classes.searchWrap}>
          <SFIcon name="search" size={15} className={classes.searchIcon} />
          <input
            className={classes.searchInput}
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search questions and tutorials…"
            aria-label="Search help content"
          />
          {searching && (
            <button
              type="button"
              className={classes.searchClear}
              onClick={() => setTerm("")}
              aria-label="Clear search"
            >
              <SFIcon name="clearSearch" size={15} />
            </button>
          )}
        </div>
        {searching && !loading && (
          <span className={classes.resultCount}>
            {faqMatches} {faqMatches === 1 ? "question" : "questions"} · {tutorialMatches}{" "}
            {tutorialMatches === 1 ? "tutorial" : "tutorials"}
          </span>
        )}
      </div>

      <div className={`${classes.panels} help-panels`}>
        <Panel
          title="Frequently asked questions"
          subtitle="Quick answers to the questions we get most."
          width="62%"
          loading={loading}
          error={errors.faqs}
          onRetry={retry}
          isEmpty={!faqMatches}
          emptyState={
            searching ? (
              <EmptyState
                type="filtered"
                heading="No matching questions"
                subtext={`Nothing here matches “${term.trim()}”. Try a different word, or start a chat with us.`}
              />
            ) : (
              <EmptyState
                icon="info"
                heading="No questions yet"
                subtext="FAQs will show up here as soon as they're available."
              />
            )
          }
        >
          {visibleFaqs.map((faq) => (
            <FaqItem key={faq._id} faq={faq} open={isOpen(faq._id)} onToggle={() => toggle(faq._id)} />
          ))}
        </Panel>

        <Panel
          title="Video tutorials"
          subtitle="Short walkthroughs of the features you use every day."
          width="36%"
          loading={loading}
          error={errors.tutorials}
          onRetry={retry}
          isEmpty={!tutorialMatches}
          emptyState={
            searching ? (
              <EmptyState
                type="filtered"
                heading="No matching tutorials"
                subtext={`Nothing here matches “${term.trim()}”.`}
              />
            ) : (
              <EmptyState
                icon="info"
                heading="No tutorials yet"
                subtext="Video walkthroughs will show up here as soon as they're available."
              />
            )
          }
        >
          {renderGroups(tutorialGroups, (tutorial) => (
            <TutorialCard key={tutorial._id} tutorial={tutorial} />
          ))}
        </Panel>
      </div>
    </section>
  );
};

// Help content is readable by any authenticated user — no plan gate and no
// role condition, so this only needs the httpClient/user injection, not the
// authorization checks the shop-scoped pages carry.
export default withAuthentication(Help);
